import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { VersionedDataContext } from "../versioned_data/context";

type RowEditState = {
  committedValues: Record<string, string>;
  originalValues: Record<string, string>;
  approved?: boolean; // Internal field - not saved to S3, tracks approval status
};

// Combined context that includes both the unsaved guard and edits functionality
export const SupplyWalkEditsContext = createContext<{
  hasUnapprovedEdits: () => boolean;
  hasAnyApprovedRows: () => boolean;
  setApprovedRowCount: (count: number) => void;
  incrementApprovedRowCount: () => void;
  decrementApprovedRowCount: () => void;
  discardChanges: () => void;
  setRowApproved: (rowKey: string, approved: boolean) => void;
  removeRowEdit: (rowKey: string) => void;
  storeEdit: (rowKey: string, editState: RowEditState) => void;
  getEdit: (rowKey: string) => RowEditState | undefined;
} | null>(null);

export const useSupplyWalkEdits = () => {
  const ctx = useContext(SupplyWalkEditsContext);
  return ctx;
};

// Global provider for supply walk edits
export const SupplyWalkEditsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Global storage for unapproved edits (shared across all instances)
  const globalEditStorage = useRef<Map<string, RowEditState>>(new Map());

  // Track approved row count from table data
  const approvedRowCountRef = useRef<number>(0);

  // Get the main run from context to detect run changes
  const { mainRun } = useContext(VersionedDataContext);

  // Note: Storage is now run-specific due to runAttemptId in keys, so no need to clear on run change
  // The keys automatically isolate edits by run: `${poId}-${sku}-${location}-${supplier}-${runAttemptId}`
  // But we still need to dispatch events to notify other components
  useEffect(() => {
    // Dispatch event to trigger cache invalidation in other components
    window.dispatchEvent(new CustomEvent("runChanged"));
  }, [mainRun]);

  const hasUnapprovedEdits = useCallback(() => {
    if (!globalEditStorage.current) return false;

    for (const [, editState] of globalEditStorage.current) {
      if (editState.approved !== true) {
        return true;
      }
    }
    return false;
  }, []);

  const hasAnyApprovedRows = useCallback(() => {
    return approvedRowCountRef.current > 0;
  }, []);

  const setApprovedRowCount = useCallback((count: number) => {
    approvedRowCountRef.current = count;
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const incrementApprovedRowCount = useCallback(() => {
    approvedRowCountRef.current += 1;
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const decrementApprovedRowCount = useCallback(() => {
    approvedRowCountRef.current = Math.max(0, approvedRowCountRef.current - 1);
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const storeEdit = useCallback((rowKey: string, editState: RowEditState) => {
    globalEditStorage.current.set(rowKey, editState);
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const getEdit = useCallback((rowKey: string) => {
    return globalEditStorage.current.get(rowKey);
  }, []);

  const setRowApproved = useCallback((rowKey: string, approved: boolean) => {
    const editState = globalEditStorage.current.get(rowKey);
    if (editState) {
      editState.approved = approved;
    } else {
      // If editState doesn't exist, create a minimal one to track approval status
      // This ensures we can track approval even when there are no pending edits
      globalEditStorage.current.set(rowKey, {
        committedValues: {},
        originalValues: {},
        approved: approved,
      });
    }
    // Dispatch event to trigger guard recompute and notify nested details
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
    // Dispatch approval toggle event for nested details component
    try {
      window.dispatchEvent(
        new CustomEvent("poApprovalToggled", {
          detail: { rowKey, checked: approved },
        })
      );
    } catch (_) {}
  }, []);

  const removeRowEdit = useCallback((rowKey: string) => {
    globalEditStorage.current.delete(rowKey);
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const discardChanges = useCallback(() => {
    globalEditStorage.current.clear();
    // Dispatch event to trigger guard recompute
    window.dispatchEvent(new CustomEvent("supplyEditsChanged"));
  }, []);

  const value = useMemo(
    () => ({
      hasUnapprovedEdits,
      hasAnyApprovedRows,
      setApprovedRowCount,
      incrementApprovedRowCount,
      decrementApprovedRowCount,
      discardChanges,
      setRowApproved,
      removeRowEdit,
      storeEdit,
      getEdit,
    }),
    [
      hasUnapprovedEdits,
      hasAnyApprovedRows,
      setApprovedRowCount,
      incrementApprovedRowCount,
      decrementApprovedRowCount,
      discardChanges,
      setRowApproved,
      removeRowEdit,
      storeEdit,
      getEdit,
    ]
  );
  return (
    <SupplyWalkEditsContext.Provider value={value}>
      {children}
    </SupplyWalkEditsContext.Provider>
  );
};
